import React, { useEffect, useState } from "react";
import { Network, Loader2, Bookmark, Upload, Check, Plus, X, Swords, MapPin, ShieldQuestion, Clock, Sparkles, GitBranch } from "lucide-react";
import { runConnectDots, publishConnection } from "../lib/api";
import { isConnectionSaved, toggleConnectionSaved } from "../lib/bookmarks";
import { renderMarkdown } from "../lib/markdown";
import { logSearchEvent, useTrackPageView } from "../hooks/useAnalytics";
import { useDossierProgress } from "../hooks/useDossierProgress";
import SourceTriangulator from "../components/SourceTriangulator";
import PageHeader from "../components/PageHeader";
import ConnectionChat from "../components/ConnectionChat";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { celebrateNewSigils } from "../lib/sigilCelebration";

const DRAFT_KEY = "vault.connect.draft.v1";
const ACTIVE_KEY = "vault.connect.active.v1";

const loadDraft = () => {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
};
const saveDraft = (d) => {
  if (d) localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  else localStorage.removeItem(DRAFT_KEY);
};
const loadActive = () => {
  try { return JSON.parse(localStorage.getItem(ACTIVE_KEY) || "null"); } catch { return null; }
};
const saveActive = (a) => {
  if (a) localStorage.setItem(ACTIVE_KEY, JSON.stringify(a));
  else localStorage.removeItem(ACTIVE_KEY);
};

export default function ConnectDotsPage() {
  const draft = loadDraft() || { items: [], input: "", context: "" };
  const active = loadActive();
  const [items, setItems] = useState(draft.items || []);
  const [input, setInput] = useState("");
  const [context, setContext] = useState(draft.context || "");
  const [loading, setLoading] = useState(false);
  const [realProgress, setRealProgress] = useState([]);
  const [result, setResult] = useState(active);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(active ? isConnectionSaved(active.id) : false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(active ? !!active.is_published : false);
  const [allowCosigns, setAllowCosigns] = useState(true);  // author choice at publish time
  // FACTION / CONFLICT MODE — when on, the page swaps the single-bar input
  // for dedicated per-side input bars. Each side keeps its OWN list of
  // subjects so users can build factions cleanly without manual chip-cycling.
  const [factionMode, setFactionMode] = useState(!!draft.factionMode);
  const [sideItems, setSideItems] = useState(draft.sideItems || { A: [], B: [], C: [] });
  const [sideInputs, setSideInputs] = useState({ A: "", B: "", C: "" });
  const [sideCEnabled, setSideCEnabled] = useState(!!draft.sideCEnabled);
  // Analysis mode — default / geographic / skeptic / timeline / occult. Persisted
  // as part of the draft so a refresh keeps the user's selection.
  const [mode, setMode] = useState(draft.mode || "default");
  const navigate = useNavigate();
  const progressLines = useDossierProgress(loading, realProgress);
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    if (!loading) { setElapsedSec(0); return undefined; }
    const start = Date.now();
    const t = setInterval(() => setElapsedSec(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [loading]);
  // Pre-fill items + context if user came here from a "Negative Space →
  // Investigate" click on an artifact page.
  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem("vault.connect.prefill");
      if (raw) {
        const pre = JSON.parse(raw);
        if (Array.isArray(pre.items) && pre.items.length > 0) {
          setItems(pre.items.slice(0, 12));
          if (pre.context) setContext(String(pre.context).slice(0, 200));
          setTimeout(() => {
            document.querySelector("[data-testid=connect-submit]")?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 200);
        }
      }
    } catch { /* ignore */ }
    sessionStorage.removeItem("vault.connect.prefill");
    // ALSO support inbound ?subjects=A|B|C from the Equation Builder bridge
    // (or any other surface that wants to seed the workspace via URL).
    try {
      const qs = new URLSearchParams(window.location.search);
      const raw = qs.get("subjects");
      if (raw) {
        const parsed = raw.split("|")
          .map((s) => s.trim())
          .filter((s) => s && s.length <= 120)
          .slice(0, 12);
        if (parsed.length > 0) {
          setItems(parsed);
          // Strip the param so refreshing doesn't re-trigger.
          window.history.replaceState({}, "", "/connect-dots");
        }
      }
    } catch { /* ignore */ }
  }, []);

  useTrackPageView("/connect-dots");

  // Persist draft inputs
  React.useEffect(() => {
    if (!result) saveDraft({ items, context, mode, factionMode, sideItems, sideCEnabled });
  }, [items, context, mode, factionMode, sideItems, sideCEnabled, result]);

  // Persist active result
  React.useEffect(() => {
    saveActive(result);
  }, [result]);

  // Toggling Faction Mode migrates state so users never lose work:
  //  • OFF → ON: dump existing single-bar items into Side A as a starting point.
  //  • ON → OFF: flatten all sides back into the single items list.
  const toggleFactionMode = () => {
    if (loading) return;
    if (!factionMode) {
      // Going ON — seed Side A with current items (if empty) so the user
      // doesn't have to retype anything.
      setSideItems((prev) => {
        const hasAny = (prev.A?.length || 0) + (prev.B?.length || 0) + (prev.C?.length || 0) > 0;
        if (hasAny) return prev;
        return { A: items.slice(0, 12), B: [], C: [] };
      });
      setFactionMode(true);
    } else {
      // Going OFF — merge all sides back into items (dedupe).
      const merged = [...sideItems.A, ...sideItems.B, ...(sideCEnabled ? sideItems.C : [])];
      const dedup = [];
      const seen = new Set();
      for (const m of merged) {
        const k = m.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        dedup.push(m);
        if (dedup.length >= 12) break;
      }
      setItems(dedup);
      setFactionMode(false);
    }
  };

  // Derived: the actual list of subjects we'll send to the backend.
  const effectiveItems = factionMode
    ? [
        ...sideItems.A,
        ...sideItems.B,
        ...(sideCEnabled ? sideItems.C : []),
      ]
    : items;

  const addItem = () => {
    const v = input.trim();
    if (!v) return;
    if (items.includes(v)) {
      toast.error("Already added");
      return;
    }
    if (items.length >= 12) {
      toast.error("Max 12 subjects");
      return;
    }
    setItems([...items, v]);
    setInput("");
  };

  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  // FACTION MODE helpers — each side has its own input bar + chips.
  const addToSide = (side) => {
    const v = (sideInputs[side] || "").trim();
    if (!v) return;
    const total = sideItems.A.length + sideItems.B.length + sideItems.C.length;
    if (total >= 12) {
      toast.error("Max 12 subjects total across sides");
      return;
    }
    // Reject duplicates across ALL sides — same subject can't be on two teams.
    const lc = v.toLowerCase();
    for (const k of ["A", "B", "C"]) {
      if (sideItems[k].some((x) => x.toLowerCase() === lc)) {
        toast.error(`Already on Side ${k}`);
        return;
      }
    }
    setSideItems({ ...sideItems, [side]: [...sideItems[side], v] });
    setSideInputs({ ...sideInputs, [side]: "" });
  };
  const removeFromSide = (side, idx) => {
    setSideItems({
      ...sideItems,
      [side]: sideItems[side].filter((_, i) => i !== idx),
    });
  };
  const clearSide = (side) => {
    setSideItems({ ...sideItems, [side]: [] });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (effectiveItems.length < 2) {
      toast.error(factionMode ? "Add at least 2 subjects across sides" : "Add at least 2 subjects");
      return;
    }
    if (effectiveItems.length > 12) {
      toast.error("Max 12 subjects total");
      return;
    }
    // Build factions payload from the bucket arrays IF faction mode is ON
    // AND at least two sides have ≥1 member.
    let factionsPayload = null;
    if (factionMode) {
      const grouped = [];
      if (sideItems.A.length) grouped.push(sideItems.A);
      if (sideItems.B.length) grouped.push(sideItems.B);
      if (sideCEnabled && sideItems.C.length) grouped.push(sideItems.C);
      if (grouped.length < 2) {
        toast.error("Fraction mode needs at least 2 sides with subjects");
        return;
      }
      factionsPayload = grouped;
    }
    setLoading(true);
    setErr(null);
    setResult(null);
    setRealProgress([]);
    logSearchEvent("search_connect", effectiveItems.join(" + "));
    try {
      const r = await runConnectDots(
        effectiveItems,
        context.trim() || null,
        (events) => setRealProgress(events),
        { factions: factionsPayload, mode },
      );
      setResult(r);
      setSaved(isConnectionSaved(r.id));
      setPublished(!!r.is_published);
      // Running a connection unlocks connector / cartographer sigils
      celebrateNewSigils().catch(() => {});
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "";
      setErr(detail || "Connection engine failure.");
    } finally {
      setLoading(false);
    }
  };

  const onSave = () => {
    if (!result) return;
    const next = toggleConnectionSaved(result.id);
    setSaved(next);
    toast.success(next ? "Saved to your library" : "Removed from your library");
  };

  const onPublish = async () => {
    if (!result || published || publishing) return;
    setPublishing(true);
    try {
      await publishConnection(result.id, { cosignsAllowed: allowCosigns });
      setPublished(true);
      // Auto-bookmark on publish so Eclipse Mode survival progress and sigil
      // unlocks fire from a single click. Mirrors DeepDivePage.onPublish.
      if (!isConnectionSaved(result.id)) {
        toggleConnectionSaved(result.id);
        setSaved(true);
      }
      toast.success("Published to Connecting Dots archive");
    } catch {
      toast.error("Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  // Wipe the persisted analysis from localStorage so the page returns to a fresh state.
  // Also clears the subject input + draft items so it's a true "start over". No confirm — the
  // page header is clear about what gets wiped, and Undo lives in the browser back button.
  const onClear = () => {
    setResult(null);
    setSaved(false);
    setPublished(false);
    setErr(null);
    setItems([]);
    setInput("");
    setContext("");
    setRealProgress([]);
    setMode("default");
    setFactionMode(false);
    setSideItems({ A: [], B: [], C: [] });
    setSideInputs({ A: "", B: "", C: "" });
    setSideCEnabled(false);
    saveActive(null);
    saveDraft({ items: [], input: "", context: "", mode: "default", factionMode: false, sideItems: { A: [], B: [], C: [] }, sideCEnabled: false });
    toast.success("Workspace cleared");
  };

  return (
    <div data-testid="connect-dots-page">
      <PageHeader
        color="#f472b6"
        label="Connect The Dots"
        icon={<Network size={11} />}
        title={<>The <span className="text-pink-400">Crossreference.</span></>}
        subtitle={<>Type any combination of people, places, events, or ideas — the analyst will hunt every documented link between them. Six modes from <strong className="text-pink-400">Default</strong> to <strong className="text-pink-400">Bloodline</strong>, plus <strong className="text-pink-400">Faction Mode</strong> for rival-camp inputs. <span className="text-pink-300">Everything is one big link.</span></>}
        testid="connect-dots-header"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">

      {/* CROSSREFERENCE PANEL — header strip + Fraction toggle + Analysis Mode + Subjects + Context */}
      <section className="border border-pink-400/30 bg-[#0a0a0a] mb-8" data-testid="connect-crossreference-panel">
        <div className="px-5 py-3 border-b border-pink-400/30 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Network size={12} className="text-pink-400" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-pink-400">// CROSSREFERENCE</span>
            <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 ml-3">
              ADD 2 TO 12 SUBJECTS
            </span>
          </div>
          <button
            type="button"
            onClick={toggleFactionMode}
            data-testid="connect-faction-toggle"
            disabled={loading}
            className={`inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 transition-colors bg-[#050505] ${
              factionMode
                ? "border-[#ff3b30]/60 text-[#ff3b30]"
                : "border-white/15 text-zinc-400 hover:text-[#ff3b30] hover:border-[#ff3b30]/50"
            }`}
            title="Group subjects into rival fractions — the analyst will focus on cross-fraction links"
          >
            <Swords size={11} /> FRACTION MODE {factionMode ? "· ON" : ""}
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* ANALYSIS MODE chips */}
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">
              ANALYSIS MODE — HOW THE ANALYST READS THE LINKS
            </div>
            <div className="flex flex-wrap gap-2" data-testid="connect-mode-group">
              {[
                { id: "default",    label: "DEFAULT",     icon: Network,         color: "#f472b6", desc: "Full investigative connection dossier — every angle." },
                { id: "geographic", label: "GEOGRAPHIC",  icon: MapPin,          color: "#22d3ee", desc: "Map every shared address, flight log, venue, and jurisdiction." },
                { id: "skeptic",    label: "SKEPTIC",     icon: ShieldQuestion,  color: "#ffd60a", desc: "Steelman the debunker case — why the connection might be bunk." },
                { id: "timeline",   label: "TIMELINE",    icon: Clock,           color: "#a78bfa", desc: "Strict chronological ledger of every documented intersection." },
                { id: "occult",     label: "OCCULT",      icon: Sparkles,        color: "#ec4899", desc: "Surface symbols, numerology, sigils, dates, and ritual motifs — ARG fuel." },
                { id: "bloodline",  label: "BLOODLINE",   icon: GitBranch,       color: "#fb923c", desc: "Family trees, marriages, godparents, adoptions, and dynastic alliances." },
              ].map((m) => {
                const Icon = m.icon;
                const active = mode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    disabled={loading}
                    onClick={() => setMode(m.id)}
                    title={m.desc}
                    data-testid={`connect-mode-${m.id}`}
                    className={`inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 transition-colors disabled:opacity-50 bg-[#050505] ${
                      active ? "text-white" : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                    }`}
                    style={active ? { borderColor: `${m.color}99`, color: m.color, boxShadow: `inset 0 0 0 1px ${m.color}33` } : undefined}
                  >
                    <Icon size={11} /> {m.label}{active ? " · ON" : ""}
                  </button>
                );
              })}
            </div>
          </div>

          {/* SUBJECT INPUT — single bar in default mode, dedicated per-side bars when Faction Mode is on. */}
          {!factionMode ? (
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">
                SUBJECTS
              </div>
              <form onSubmit={(e) => { e.preventDefault(); addItem(); }}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="e.g. Jeffrey Epstein, MIT, Bohemian Grove..."
                    data-testid="connect-input"
                    className="flex-1 bg-[#050505] border border-white/15 px-4 py-3 font-mono text-sm text-white placeholder-zinc-600 focus:border-pink-400 focus:outline-none"
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || items.length >= 12}
                    data-testid="connect-add-item"
                    className="inline-flex items-center gap-2 bg-[#050505] border border-white/20 text-zinc-300 hover:text-pink-400 hover:border-pink-400/50 disabled:opacity-40 px-4 py-3 font-mono text-xs uppercase tracking-[0.18em]"
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
              </form>

              {items.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {items.map((it, i) => (
                    <span
                      key={i}
                      data-testid={`connect-chip-${i}`}
                      className="inline-flex items-center gap-2 font-mono text-xs tracking-wider border border-pink-400/40 bg-[#050505] text-pink-300 px-3 py-1.5"
                    >
                      {it}
                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        className="hover:text-[#ff3b30]"
                        aria-label="Remove"
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => { setItems([]); setInput(""); }}
                    data-testid="connect-clear-subjects"
                    className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-[#ff3b30] inline-flex items-center gap-1 ml-1"
                  >
                    <X size={10} /> CLEAR ALL
                  </button>
                </div>
              )}
            </div>
          ) : (
        <div className="mb-6 space-y-3" data-testid="connect-faction-panels">
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#ff3b30] mb-2 flex items-center gap-2">
            <Swords size={11} /> FRACTION MODE — BUILD EACH SIDE SEPARATELY · CROSS-SIDE LINKS GET PRIORITY
          </div>
          {(["A", "B", ...(sideCEnabled ? ["C"] : [])]).map((side) => {
            const colors = { A: "#22d3ee", B: "#ff3b30", C: "#ffd60a" };
            const c = colors[side];
            const placeholders = {
              A: "e.g. CIA, DARPA, Lockheed Martin...",
              B: "e.g. Soviet Union, KGB, Cuban exiles...",
              C: "e.g. press intermediaries, fixers, third-party orgs...",
            };
            const list = sideItems[side] || [];
            return (
              <div
                key={side}
                data-testid={`connect-side-panel-${side}`}
                className="border bg-[#050505] p-4"
                style={{ borderColor: `${c}55` }}
              >
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div
                    className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold"
                    style={{ color: c }}
                  >
                    SIDE {side} — {list.length} {list.length === 1 ? "SUBJECT" : "SUBJECTS"}
                  </div>
                  {side === "C" && (
                    <button
                      type="button"
                      onClick={() => { setSideCEnabled(false); setSideItems({ ...sideItems, C: [] }); }}
                      data-testid="connect-side-c-remove"
                      disabled={loading}
                      className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-[#ff3b30] inline-flex items-center gap-1"
                    >
                      <X size={10} /> REMOVE SIDE C
                    </button>
                  )}
                </div>
                <form
                  onSubmit={(e) => { e.preventDefault(); addToSide(side); }}
                  className="flex gap-2 mb-2"
                >
                  <input
                    type="text"
                    value={sideInputs[side]}
                    onChange={(e) => setSideInputs({ ...sideInputs, [side]: e.target.value })}
                    placeholder={placeholders[side]}
                    data-testid={`connect-side-input-${side}`}
                    className="flex-1 bg-[#050505] border px-3 py-2 font-mono text-sm text-white placeholder-zinc-600 focus:outline-none"
                    style={{ borderColor: `${c}55` }}
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={loading || !sideInputs[side].trim()}
                    data-testid={`connect-side-add-${side}`}
                    className="inline-flex items-center gap-1.5 border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] disabled:opacity-40"
                    style={{ borderColor: `${c}55`, color: c }}
                  >
                    <Plus size={12} /> Add to {side}
                  </button>
                </form>
                {list.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {list.map((it, i) => (
                      <span
                        key={i}
                        data-testid={`connect-side-chip-${side}-${i}`}
                        className="inline-flex items-center gap-2 font-mono text-xs tracking-wider border bg-[#0a0a0a] px-3 py-1.5"
                        style={{ borderColor: `${c}99`, color: c }}
                      >
                        {it}
                        <button
                          type="button"
                          onClick={() => removeFromSide(side, i)}
                          className="hover:text-[#ff3b30]"
                          aria-label="Remove"
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={() => clearSide(side)}
                      data-testid={`connect-side-clear-${side}`}
                      className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-[#ff3b30] inline-flex items-center gap-1 ml-1"
                    >
                      <X size={10} /> CLEAR
                    </button>
                  </div>
                ) : (
                  <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-600">
                    NO SUBJECTS YET · ADD AT LEAST 1 TO ENGAGE THIS SIDE
                  </div>
                )}
              </div>
            );
          })}
          {!sideCEnabled && (
            <button
              type="button"
              onClick={() => setSideCEnabled(true)}
              data-testid="connect-side-c-add"
              disabled={loading}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase border border-[#ffd60a]/50 text-[#ffd60a] px-3 py-1.5 hover:bg-[#ffd60a]/10 disabled:opacity-40"
            >
              <Plus size={11} /> ADD SIDE C (3-WAY CONFLICT)
            </button>
          )}
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
            TOTAL: {effectiveItems.length}/12 SUBJECTS · MIN 2 SIDES MUST HAVE ≥1 SUBJECT
          </div>
        </div>
      )}

          {/* CONTEXT input — inside the consolidated CROSSREFERENCE panel */}
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">
              CONTEXT — OPTIONAL FRAMING
            </div>
            <input
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="e.g. focus on financial connections post-2010"
              data-testid="connect-context"
              className="w-full bg-[#050505] border border-white/15 px-4 py-3 font-mono text-sm text-white placeholder-zinc-600 focus:border-pink-400 focus:outline-none"
              disabled={loading}
            />
          </div>
        </div>
      </section>

      <button
        onClick={submit}
        disabled={loading || effectiveItems.length < 2}
        data-testid="connect-submit"
        className="inline-flex items-center gap-2 bg-pink-500 text-white hover:bg-white hover:text-[#050505] disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors mb-10"
      >
        {loading ? (
          <><Loader2 size={14} className="animate-spin" /> Tracing Connections...</>
        ) : (
          <><Network size={14} /> Find the Links</>
        )}
      </button>

      {err && (
        <div data-testid="connect-error" className="border border-[#ff3b30]/50 bg-[#ff3b30]/5 p-5 mb-8 font-mono text-xs text-[#ff3b30]">
          ERROR — {err}
        </div>
      )}

      {loading && (
        <div
          data-testid="cd-progress-shell"
          className="border border-white/10 bg-[#0a0a0a] p-6 sm:p-8 font-mono text-xs text-zinc-400"
        >
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
            <span className="text-[10px] tracking-[0.25em] text-pink-400">
              ◉ CONNECTIONS ANALYST // ACTIVE — DO NOT CLOSE
            </span>
            <span className="text-[10px] tracking-[0.25em] text-zinc-600 tabular-nums">
              T+{elapsedSec.toString().padStart(2, "0")}s · POLLING /api/connect-dots
            </span>
          </div>
          <div className="space-y-1 leading-relaxed">
            {progressLines.map((line, i) => {
              const isLatest = i === progressLines.length - 1;
              const isReal = line.kind === "real";
              const showDelta = isReal && line.deltaMs > 100;
              const deltaTxt = !showDelta ? "" : line.deltaMs < 1000
                ? `+${line.deltaMs}ms`
                : `+${(line.deltaMs / 1000).toFixed(1)}s`;
              return (
                <div
                  key={i}
                  className={`flex gap-2 ${isLatest ? "text-white" : (isReal ? "text-zinc-300" : "text-zinc-500")}`}
                  data-testid={`cd-progress-line-${i}`}
                >
                  <span className={isReal ? "text-pink-400" : "text-zinc-700"}>
                    {isReal ? "●" : ">"}
                  </span>
                  <span className="whitespace-pre-wrap break-words flex-1 min-w-0">{line.label}</span>
                  {showDelta && (
                    <span className="text-zinc-600 text-[10px] tracking-[0.15em] tabular-nums whitespace-nowrap self-center">
                      ({deltaTxt})
                    </span>
                  )}
                  {isLatest && (
                    <span
                      className={`ml-1 inline-block w-1.5 h-3 animate-pulse self-end ${isReal ? "bg-pink-400" : "bg-zinc-500"}`}
                      aria-hidden="true"
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-5 pt-3 border-t border-white/10 text-[10px] tracking-[0.25em] text-[#ffd60a]">
            &gt; usually 15–45 seconds · longer for ≥6 subjects · linkage graph will appear below
          </div>
        </div>
      )}

      {result && (
        <div data-testid="connect-result" className="bg-[#0F0F0F] border-t-4 border-t-pink-400 border-x border-b border-white/10 p-6 md:p-10">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10 flex-wrap gap-3">
            <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] text-zinc-500">
              <Network size={12} />
              CONNECTION ANALYSIS // ID {result.id.slice(0, 8).toUpperCase()}
              {result.mode && result.mode !== "default" && (
                <span
                  data-testid="connect-result-mode"
                  className="ml-2 inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase"
                  style={(() => {
                    const c = result.mode === "geographic" ? "#22d3ee"
                      : result.mode === "skeptic" ? "#ffd60a"
                      : result.mode === "timeline" ? "#a78bfa"
                      : result.mode === "occult" ? "#ec4899"
                      : result.mode === "bloodline" ? "#fb923c" : "#f472b6";
                    return { borderColor: `${c}99`, color: c, background: `${c}1a` };
                  })()}
                >
                  {result.mode === "geographic" ? <MapPin size={10} />
                    : result.mode === "skeptic" ? <ShieldQuestion size={10} />
                    : result.mode === "timeline" ? <Clock size={10} />
                    : result.mode === "occult" ? <Sparkles size={10} />
                    : result.mode === "bloodline" ? <GitBranch size={10} />
                    : <Network size={10} />}
                  {result.mode.toUpperCase()} MODE
                </span>
              )}
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-pink-400">
              {result.items.length} SUBJECTS
            </div>
          </div>

          <h2 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl text-white mb-6">
            {result.title}
          </h2>

          <div className="flex flex-wrap gap-3 mb-8 pb-6 border-b border-white/10">
            <button
              onClick={onSave}
              data-testid="connect-save"
              className={`inline-flex items-center gap-2 px-4 py-2 border font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
                saved
                  ? "border-[#ffd60a]/50 text-[#ffd60a] bg-[#ffd60a]/5"
                  : "border-white/20 text-zinc-300 hover:text-[#ffd60a] hover:border-[#ffd60a]/50"
              }`}
            >
              <Bookmark size={12} fill={saved ? "#ffd60a" : "none"} />
              {saved ? "Saved" : "Save to My Library"}
            </button>
            <button
              onClick={onPublish}
              disabled={published || publishing}
              data-testid="connect-publish"
              className={`inline-flex items-center gap-2 px-4 py-2 border font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
                published
                  ? "border-pink-400/50 text-pink-400 bg-pink-400/5 cursor-default"
                  : "border-white/20 text-zinc-300 hover:text-pink-400 hover:border-pink-400/50"
              }`}
            >
              {published ? <Check size={12} /> : publishing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {published ? "Published" : publishing ? "Publishing..." : "Publish to Connecting Dots Archive"}
            </button>
            {/* Cosigns gate — author choice at publish time. Hidden after publishing. */}
            {!published && (
              <label
                className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white hover:border-white/30 transition-colors cursor-pointer"
                data-testid="connect-cosigns-toggle"
              >
                <input
                  type="checkbox"
                  checked={allowCosigns}
                  onChange={(e) => setAllowCosigns(e.target.checked)}
                  className="accent-[#00ff88]"
                  data-testid="connect-cosigns-checkbox"
                />
                Allow Co-signs
              </label>
            )}
            {published && (
              <button
                onClick={() => navigate(`/connection/${result.id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 border font-mono text-[11px] tracking-[0.2em] uppercase border-white/20 text-zinc-300 hover:text-white hover:border-white/50"
              >
                View Public Page
              </button>
            )}
            <button
              onClick={onClear}
              data-testid="connect-clear"
              className="inline-flex items-center gap-2 px-4 py-2 border font-mono text-[11px] tracking-[0.2em] uppercase border-white/15 text-zinc-400 hover:text-[#ff3b30] hover:border-[#ff3b30]/50 transition-colors"
            >
              <X size={12} /> Clear Workspace
            </button>
          </div>

          <div className="markdown text-zinc-300" dangerouslySetInnerHTML={{ __html: renderMarkdown(result.analysis) }} />

          <ConnectionChat connection={result} onUpdate={(updated) => setResult(updated)} />
        </div>
      )}

      <SourceTriangulator />
      </div>
    </div>
  );
}
